/**
 * Liquid Glass Design Tokens — injected once into <head> as CSS custom properties.
 *
 * Host sites can override any --hs-* variable to adapt the widget's appearance.
 * The token set switches between dark-glass and light-glass via a data attribute
 * on the widget root ([data-hs-theme="dark"|"light"]).
 */

const STYLE_ID = 'holostaff-liquid-tokens'

/** All design tokens with dark/light variants. */
export const tokens = {
  // --- Surfaces ---
  glassBlur: '20px',
  glassSaturate: '180%',

  // Dark glass (default — white-tinted glass on dark backgrounds)
  dark: {
    glassBg: 'rgba(255, 255, 255, 0.10)',
    glassBgElevated: 'rgba(255, 255, 255, 0.16)',
    glassBgSolid: 'rgba(30, 30, 40, 0.85)',
    glassBorder: 'rgba(255, 255, 255, 0.14)',
    glassBorderHover: 'rgba(255, 255, 255, 0.28)',
    glassShadow: '0 8px 32px rgba(0, 0, 0, 0.24)',
    glassShadowElevated: '0 12px 48px rgba(0, 0, 0, 0.32)',
    textPrimary: 'rgba(255, 255, 255, 0.92)',
    textSecondary: 'rgba(255, 255, 255, 0.56)',
    textTertiary: 'rgba(255, 255, 255, 0.32)',
    divider: 'rgba(255, 255, 255, 0.08)',
  },

  // Light glass (dark-tinted glass on light backgrounds)
  light: {
    glassBg: 'rgba(255, 255, 255, 0.55)',
    glassBgElevated: 'rgba(255, 255, 255, 0.72)',
    glassBgSolid: 'rgba(255, 255, 255, 0.92)',
    glassBorder: 'rgba(0, 0, 0, 0.06)',
    glassBorderHover: 'rgba(0, 0, 0, 0.12)',
    glassShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    glassShadowElevated: '0 12px 48px rgba(0, 0, 0, 0.12)',
    textPrimary: 'rgba(0, 0, 0, 0.85)',
    textSecondary: 'rgba(0, 0, 0, 0.50)',
    textTertiary: 'rgba(0, 0, 0, 0.28)',
    divider: 'rgba(0, 0, 0, 0.06)',
  },

  // --- Radii ---
  radiusSm: '10px',
  radiusMd: '16px',
  radiusLg: '24px',
  radiusPill: '9999px',

  // --- Typography ---
  font: "-apple-system, 'Inter', 'Segoe UI', system-ui, sans-serif",
  textXs: '11px',
  textSm: '12px',
  textBase: '14px',
  textLg: '16px',
  textXl: '20px',
  weightNormal: '400',
  weightMedium: '500',
  weightSemibold: '600',
  lineHeight: '1.45',

  // --- Motion ---
  springDuration: '500ms',
  springEasing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  easeFast: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
  fadeDuration: '200ms',

  // --- Accent ---
  accent: '#7C5CFF',
  accentGlow: 'rgba(124, 92, 255, 0.35)',
  accentText: '#ffffff',

  // --- Spacing ---
  space1: '4px',
  space2: '8px',
  space3: '12px',
  space4: '16px',
  space5: '20px',
  space6: '24px',
  space8: '32px',

  // --- Z-index layers ---
  zBase: '2147483640',
  zOverlay: '2147483644',
  zControls: '2147483646',
  zTop: '2147483647',
} as const

/** Inject the CSS custom properties stylesheet into <head>. Idempotent. */
export function injectTokens(): void {
  if (document.getElementById(STYLE_ID)) return

  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
    /* Holostaff Liquid Glass Design Tokens */
    :root {
      --hs-glass-blur: ${tokens.glassBlur};
      --hs-glass-saturate: ${tokens.glassSaturate};

      --hs-radius-sm: ${tokens.radiusSm};
      --hs-radius-md: ${tokens.radiusMd};
      --hs-radius-lg: ${tokens.radiusLg};
      --hs-radius-pill: ${tokens.radiusPill};

      --hs-font: ${tokens.font};
      --hs-text-xs: ${tokens.textXs};
      --hs-text-sm: ${tokens.textSm};
      --hs-text-base: ${tokens.textBase};
      --hs-text-lg: ${tokens.textLg};
      --hs-text-xl: ${tokens.textXl};
      --hs-weight-normal: ${tokens.weightNormal};
      --hs-weight-medium: ${tokens.weightMedium};
      --hs-weight-semibold: ${tokens.weightSemibold};
      --hs-line-height: ${tokens.lineHeight};

      --hs-spring-duration: ${tokens.springDuration};
      --hs-spring-easing: ${tokens.springEasing};
      --hs-ease-fast: ${tokens.easeFast};
      --hs-fade-duration: ${tokens.fadeDuration};

      --hs-accent: ${tokens.accent};
      --hs-accent-glow: ${tokens.accentGlow};
      --hs-accent-text: ${tokens.accentText};

      --hs-space-1: ${tokens.space1};
      --hs-space-2: ${tokens.space2};
      --hs-space-3: ${tokens.space3};
      --hs-space-4: ${tokens.space4};
      --hs-space-5: ${tokens.space5};
      --hs-space-6: ${tokens.space6};
      --hs-space-8: ${tokens.space8};
    }

    /* Dark glass (default) */
    [data-hs-theme="dark"] {
      --hs-glass-bg: ${tokens.dark.glassBg};
      --hs-glass-bg-elevated: ${tokens.dark.glassBgElevated};
      --hs-glass-bg-solid: ${tokens.dark.glassBgSolid};
      --hs-glass-border: ${tokens.dark.glassBorder};
      --hs-glass-border-hover: ${tokens.dark.glassBorderHover};
      --hs-glass-shadow: ${tokens.dark.glassShadow};
      --hs-glass-shadow-elevated: ${tokens.dark.glassShadowElevated};
      --hs-text-primary: ${tokens.dark.textPrimary};
      --hs-text-secondary: ${tokens.dark.textSecondary};
      --hs-text-tertiary: ${tokens.dark.textTertiary};
      --hs-divider: ${tokens.dark.divider};
    }

    /* Light glass */
    [data-hs-theme="light"] {
      --hs-glass-bg: ${tokens.light.glassBg};
      --hs-glass-bg-elevated: ${tokens.light.glassBgElevated};
      --hs-glass-bg-solid: ${tokens.light.glassBgSolid};
      --hs-glass-border: ${tokens.light.glassBorder};
      --hs-glass-border-hover: ${tokens.light.glassBorderHover};
      --hs-glass-shadow: ${tokens.light.glassShadow};
      --hs-glass-shadow-elevated: ${tokens.light.glassShadowElevated};
      --hs-text-primary: ${tokens.light.textPrimary};
      --hs-text-secondary: ${tokens.light.textSecondary};
      --hs-text-tertiary: ${tokens.light.textTertiary};
      --hs-divider: ${tokens.light.divider};
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      :root {
        --hs-spring-duration: 0ms;
        --hs-fade-duration: 0ms;
      }
    }
  `
  document.head.appendChild(style)
}
